import React, { useState, SetStateAction } from "react";

import { useTranslation } from "react-i18next";
import { FrontendUserInfo, OrganizationInfo } from "node-library/dist/portal";

import { Organization } from "node-library/dist/database";

import {
	CompanyIcon,
	UserGroupIcon,
} from "../../../common-components/svgIcons";
import {
	SkyBlueButton,
	CustomIconButton,
	BlueButton,
} from "../../../common-components/button";
import { Card } from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import EditOrganizationModal from "../../../common-components/modals/editOrganizationModal";
import { PrimaryColor } from "node-library/dist/colors";
import OrganizationUsersList from "./organizationUsersList";
import AddIcon from "@mui/icons-material/Add";
import AddOrganizationUserModal from "../../../common-components/modals/addOrganizationUserModal";
import ViewOrderPageSkeletonUI from "../viewOrders/skeletonUI/orderHistoryPageSkeleton";

interface Props {
	organization: OrganizationInfo;
	goBackToViewOrganizationsPage: (refresh: boolean) => void;
	gotoOrganizationDetailsPage: (organization: Organization) => void;
	adminInAction: boolean;
	frontendUser: FrontendUserInfo | undefined;
	setUnsavedChanges: React.Dispatch<SetStateAction<boolean>>;
	proceedAction: () => void;
	setProceedAction: React.Dispatch<React.SetStateAction<() => void>>;
	organizationUsers: Array<FrontendUserInfo>;
	removeUser: (organization: OrganizationInfo, user: FrontendUserInfo) => void;
	addUser: (
		organization: OrganizationInfo,
		organizationUser: FrontendUserInfo
	) => void;
	setRemoveUserModalConfirmOpen: React.Dispatch<React.SetStateAction<boolean>>;
	totalUserPageCount: number;
	loading: boolean;
}

export default function OrganizationDetailsContent(props: Props) {
	const { t: translation } = useTranslation();
	const [editOrganizationModalOpen, setEditOrganizationModalOpen] =
		useState<boolean>(false);
	const [addOrganizationUserModalOpen, setAddOrganizationUserModalOpen] =
		useState<boolean>(false);

	const {
		goBackToViewOrganizationsPage,
		gotoOrganizationDetailsPage,
		organization,
		setRemoveUserModalConfirmOpen,
		adminInAction,
		removeUser,
		addUser,
		proceedAction,
		setProceedAction,
		setUnsavedChanges,
		organizationUsers,
		loading,
	} = props;

	let actionOptions: Array<{
		children: React.ReactNode;
	}> = [
		{
			children: (
				<BlueButton
					key={`edit-organization-profile`}
					id={"edit-organization-profile-button"}
					onClick={() => {
						setEditOrganizationModalOpen(true);
					}}
					disabled={adminInAction}
				>
					{translation("EditOrganizationProfile")}
				</BlueButton>
			),
		},
	];

	const editOrganizationModal: React.ReactNode = (
		<EditOrganizationModal
			organization={organization}
			open={editOrganizationModalOpen}
			setOpen={setEditOrganizationModalOpen}
			proceedAction={proceedAction}
			setUnsavedChanges={setUnsavedChanges}
			gotoOrganizationDetailsPage={gotoOrganizationDetailsPage}
		></EditOrganizationModal>
	);

	const addOrganizationUserModal: React.ReactNode = (
		<AddOrganizationUserModal
			organization={organization}
			open={addOrganizationUserModalOpen}
			setOpen={setAddOrganizationUserModalOpen}
			proceedAction={proceedAction}
			setUnsavedChanges={setUnsavedChanges}
			gotoOrganizationDetailsPage={gotoOrganizationDetailsPage}
			addUser={addUser}
		></AddOrganizationUserModal>
	);

	const actionRow: React.ReactNode = (
		<div
			style={{ display: "flex", flexWrap: "wrap", gap: "10px", float: "right" }}
		>
			{actionOptions.map((option) => {
				return option.children;
			})}
		</div>
	);

	const companyNameTag: React.ReactNode = (
		<div className="organization-profile-name-tag">
			<div
				className="typography-caption"
				style={{
					color: "white",
				}}
			>
				{translation("CompanyName")}
			</div>
			<div
				className="typography-heading-03"
				style={{
					color: "white",
				}}
			>
				{organization.clientCompanyName}
			</div>
		</div>
	);

	const organizationAddressCard: React.ReactNode = (
		<Card className="card-border-radius-14p card-normal-padding organization-address-card">
			<div className="organization-address-content">
				<div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
					<div className="organization-address-header-icon-container">
						<CompanyIcon
							strokeColor={"#d1fae8"}
							fillColor={PrimaryColor.PRIMARY_500}
						></CompanyIcon>
					</div>
					<div className="typography-body-01 typography-bold">
						{translation("Address")}
					</div>
				</div>

				<div
					style={{
						marginTop: "20px",
						display: "flex",
						flexDirection: "column",
						gap: "10px",
					}}
				>
					<div className="organization-address-info-row typography-body-02">
						<div className="organization-address-info-row-title">
							{translation("Address")}
						</div>
						<div className="organization-address-row-info">
							{organization.address}
						</div>
					</div>
					<div className="organization-address-info-row typography-body-02">
						<div className="organization-address-info-row-title">
							{translation("City")}
						</div>
						<div className="organization-address-row-info">
							{organization.city}
						</div>
					</div>
					<div className="organization-address-info-row typography-body-02">
						<div className="organization-address-info-row-title">
							{translation("ProvinceOrState")}
						</div>
						<div className="organization-address-row-info">
							{organization.province}
						</div>
					</div>
					<div className="organization-address-info-row typography-body-02">
						<div className="organization-address-info-row-title">
							{translation("Country")}
						</div>
						<div className="organization-address-row-info">
							{organization.country}
						</div>
					</div>

					<div className="organization-address-info-row typography-body-02">
						<div className="organization-address-info-row-title">
							{translation("PostalCodeOrZip")}
						</div>
						<div className="organization-address-row-info">
							{organization.postalCode}
						</div>
					</div>
				</div>
			</div>
		</Card>
	);

	const organizationUsersList: React.ReactNode = (
		<div
			style={{
				margin: "5px",
				display: "flex",
				flexDirection: "column",
				gap: "5px",
			}}
		>
			<div className="organization-member-header">
				<div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
					<div className="organization-address-header-icon-container">
						<UserGroupIcon
							strokeColor={"#d1fae8"}
							fillColor={PrimaryColor.PRIMARY_500}
						></UserGroupIcon>
					</div>
					<div className="typography-body-01 typography-bold">
						{translation("OrganizationMember")}
					</div>
				</div>
				<SkyBlueButton
					key="delete-organization-proceed-button"
					id="delete-organization-proceed-button"
					onClick={() => {
						setAddOrganizationUserModalOpen(true);
					}}
				>
					<AddIcon style={{ fontSize: 20, marginLeft: "-5px" }} />
					<span>{translation("User")}</span>
				</SkyBlueButton>
			</div>
			{loading ? (
				<ViewOrderPageSkeletonUI />
			) : (
				<OrganizationUsersList
					users={organizationUsers}
					organization={organization}
					removeUser={removeUser}
					gotoOrganizationDetailsPage={gotoOrganizationDetailsPage}
					setRemoveUserModalConfirmOpen={setRemoveUserModalConfirmOpen}
					setProceedAction={setProceedAction}
				/>
			)}
		</div>
	);

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				gap: "20px",
				minHeight: "calc(100vh - 140px)",
				marginTop: "60px",
			}}
		>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					gap: "10px",
					flexWrap: "wrap",
				}}
			>
				<div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
					<CustomIconButton
						id="register-back-button"
						onClick={() => goBackToViewOrganizationsPage(true)}
						icon={<KeyboardArrowLeftIcon />}
					></CustomIconButton>

					<div className="typography-heading-01">
						{translation("OrganizationProfile")}
					</div>
				</div>
				{actionRow}
			</div>
			<div className="organization-profile-body">
				<div className="organization-name-and-address">
					{companyNameTag}
					{organizationAddressCard}
				</div>
				<Card
					className="card-border-radius-14p card-small-padding organization-users-list"
					key={organization.organizationId}
					sx={{ height: "fit-content", maxHeight: "100%" }}
				>
					{organizationUsersList}
				</Card>
			</div>
			{editOrganizationModal}
			{addOrganizationUserModal}
		</div>
	);
}
